import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Theme } from "@emotion/react";
import { Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, useTheme } from "@mui/material";
import { CheckedCheckBox, UncheckedCheckBox } from "@iventis/styles/src/components/checkbox-elements";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { findNode, TreeBrowserDomainNodeContext, useTreeBrowser } from "@iventis/tree-browser";
import { isAdmin } from "@iventis/utilities";
import { RootDispatch } from "./state/root.store";
import { getVersionId, onSitemapSelected, onVersionSelected } from "./sitemap-selector-helpers";
import { updateMap } from "./state/map.slice.thunks";

interface SitemapVersionSelectorProps {
    sitemapId: string;
    versionType: SitemapVersionType;
}

export const SitemapVersionSelectorComponent: React.FC<SitemapVersionSelectorProps> = ({ sitemapId, versionType }) => {
    const translate = useIventisTranslate();
    const theme = useTheme<Theme>();
    const dispatch = useDispatch<RootDispatch>();
    const { sitemaps, sitemapConfigs, mapId } = useSelector(({ mapReducer }: { mapReducer: MapGlobalState }) => ({
        // Only show available sitemaps and the sitemap configs for that map
        sitemaps: mapReducer.sitemapsInView,
        sitemapConfigs: mapReducer.sitemapConfigs,
        mapId: mapReducer.mapModule.mapId,
    }));

    const [{ mainNodeId, tree }] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "mainNodeId", "tree");

    const mapNode = useMemo(() => tree && findNode(tree?.childNodes, mainNodeId), [mainNodeId, tree]);
    const adminOnMapNode = isAdmin(mapNode?.authorisation);

    const isVersionTypeinSitemap = (sitemap: Sitemap, versionType: SitemapVersionType) => sitemap.versions.some((version) => version.type === versionType);

    const isOnlyVersionTypeinSitemap = (sitemap: Sitemap, versionType: SitemapVersionType) => sitemap.versions.every((version) => version.type === versionType);

    const getVersion = useCallback(
        (sitemapId: string, versionType: SitemapVersionType) => {
            const versionid = getVersionId(sitemapId, sitemapConfigs, sitemaps, versionType);
            return versionid;
        },
        [sitemapConfigs]
    );

    const handleSelectVersion = (e: React.ChangeEvent<HTMLInputElement>, sitemapId: string) => {
        const updatedMap = onVersionSelected(mapId, sitemaps, sitemapConfigs, sitemapId, e.target.value);
        dispatch(updateMap({ map: updatedMap, skipSave: !adminOnMapNode }));
    };

    const handleSelectDeselectSitemapVersionType = (e: React.ChangeEvent<HTMLInputElement>, sitemapId: string, versionType: SitemapVersionType) => {
        const updatedMap = onSitemapSelected(mapId, sitemaps, sitemapConfigs, sitemapId, e.target.checked, versionType);
        dispatch(updateMap({ map: updatedMap, skipSave: !adminOnMapNode }));
    };

    const sitemap = sitemaps.find((s) => s.id === sitemapId);

    return (
        <div key={`${sitemap.id}-${versionType}`}>
            {isVersionTypeinSitemap(sitemap, versionType) && !isOnlyVersionTypeinSitemap(sitemap, versionType) && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={
                                !sitemapConfigs.some((s) => {
                                    if (versionType === SitemapVersionType.TwoDimensional) {
                                        return s.sitemapId === sitemap.id && s.version2dVisible === false;
                                    }
                                    if (versionType === SitemapVersionType.ThreeDimensional) {
                                        return s.sitemapId === sitemap.id && s.version3dVisible === false;
                                    }
                                    return false;
                                })
                            }
                            icon={<UncheckedCheckBox size={15} borderStyle={`1px solid ${theme.typographyColors.subdued}`} backgroundColour="none" />}
                            checkedIcon={
                                <CheckedCheckBox size={15} tickColour={theme.shades.two} borderStyle={`1px solid ${theme.typographyColors.subdued}`} backgroundColour="none" />
                            }
                            name={sitemap.name}
                            id={`${sitemap.id}-${versionType}`}
                            color="default"
                            onChange={(e) => {
                                handleSelectDeselectSitemapVersionType(e, sitemap.id, versionType);
                            }}
                            data-testid={`${sitemap.name}-${versionType}-checkbox-sitemap-version`}
                        />
                    }
                    label={
                        versionType === SitemapVersionType.TwoDimensional
                            ? translate(Content.map10.sitemapVersion.twoDimensional)
                            : versionType === SitemapVersionType.ThreeDimensional
                            ? translate(Content.map10.sitemapVersion.threeDimensional)
                            : versionType
                    }
                    htmlFor={`${sitemap.id}-${versionType}`}
                    style={{ marginLeft: "20px" }}
                />
            )}
            <FormControl style={{ display: "block" }}>
                <RadioGroup aria-label={`${sitemap.name} versions`} value={getVersion(sitemap.id, versionType)} onChange={(e) => handleSelectVersion(e, sitemap.id)}>
                    {sitemap.versions
                        .filter((version) => version.type === versionType)
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map((version) => (
                            <FormControlLabel
                                key={version.id}
                                value={version.id}
                                control={<Radio size="small" />}
                                label={
                                    <span>
                                        <span>{version.name}</span>
                                        {version.date && (
                                            <span style={{ color: theme.typographyColors.subdued, marginLeft: "4px", fontSize: "0.85em" }}>
                                                ({new Date(version.date).toLocaleDateString(undefined, { month: "short", year: "numeric" })})
                                            </span>
                                        )}
                                    </span>
                                }
                                data-testid={`${version.name}-radio-button`}
                            />
                        ))}
                </RadioGroup>
            </FormControl>
        </div>
    );
};
